import { Component, computed, effect, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
	selector: 'countdown-timer-widget',
	standalone: true,
	imports: [],
	templateUrl: './countdown-timer.widget.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: [`./countdown-timer.widget.scss`],
})
export class CountdownTimerWidget {

	// Input: deadlineUTC timestamp
	public deadlineUTC = input.required<number>();

	// Output: emit isDeadlinePassed to parent
	public isDeadlinePassed = output<boolean>();

	// Current time updated every second
	private currentTime = signal(Date.now() / 1000);

	// Timer interval reference
	private intervalId: number | undefined;

	constructor() {
		// Start the timer when component initializes
		this.startTimer();

		// Clean up timer on destroy
		effect((onCleanup) => {
			onCleanup(() => {
				if (this.intervalId) {
					clearInterval(this.intervalId);
				}
			});
		});

		toObservable(this.timeDifference).subscribe((diff) => {
			const isPassed = diff <= 0;
			this.isDeadlinePassed.emit(isPassed);
		})
	}

	private startTimer() {
		// Update current time every second
		this.intervalId = window.setInterval(() => {
			this.currentTime.set(Date.now() / 1000);
		}, 1000);
	}

	// Calculate time difference from now
	public timeDifference = computed(() => {
		const deadline = this.deadlineUTC();
		const now = this.currentTime();
		return deadline - now;
	});

	// Format time difference as Days, Hours, Mins, Secs
	public formattedTime = computed(() => {
		const diff = Math.abs(this.timeDifference());
		const days = Math.floor(diff / 86400);
		const hours = Math.floor((diff % 86400) / 3600);
		const minutes = Math.floor((diff % 3600) / 60);
		const seconds = Math.floor(diff % 60);

		return {
			days,
			hours,
			minutes,
			seconds,
			isNegative: this.timeDifference() < 0
		};
	});

	// Display string for the countdown
	public displayTime = computed(() => {
		const time = this.formattedTime();
		const prefix = time.isNegative ? '-' : '';
		return `${prefix}${time.days}d ${time.hours}h ${time.minutes}m ${time.seconds}s`;
	});
}
